import { useMemo } from 'react';
import { Card, Typography, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import PartnerLink from '../Partners/PartnerLink';

/**
 * Displays active cleaning rules for building units
 * Shows "missing information" if no active rules exist
 */
const BuildingUnitsActiveCleanings = ({ buildingUnits }) => {
  const { t } = useTranslation();

  const activeCleaningsData = useMemo(() => {
    if (!buildingUnits || !Array.isArray(buildingUnits)) {
      return [];
    }

    const now = moment();

    return buildingUnits.map((unit) => {
      if (!unit.service_rrules || unit.service_rrules.length === 0) {
        return {
          id: unit.id,
          buildingId: unit.building_id,
          address: `${unit.street} ${unit.house_number}`,
          partner: null,
          dateFrom: null,
          dateTo: null,
          missingInfo: true,
        };
      }

      // Filter rules active on current date
      const activeRrules = unit.service_rrules.filter((rrule) => {
        const dateFrom = moment(rrule.date_from);
        const dateTo = rrule.date_to ? moment(rrule.date_to) : null;

        return dateFrom.isSameOrBefore(now, 'day') && 
               (!dateTo || dateTo.isSameOrAfter(now, 'day'));
      });

      if (activeRrules.length === 0) {
        return {
          id: unit.id,
          buildingId: unit.building_id,
          address: `${unit.street} ${unit.house_number}`,
          partner: null,
          dateFrom: null,
          dateTo: null,
          missingInfo: true,
        };
      }

      const activeRrule = activeRrules.reduce((best, current) => {
        if (!current.date_to && best.date_to) {
          return current;
        }

        if (best.date_to && !current.date_to) {
          return best;
        }

        return current.id > best.id ? current : best;
      });

      return {
        id: unit.id,
        buildingId: unit.building_id,
        address: `${unit.street} ${unit.house_number}`,
        partner: activeRrule.partner,
        dateFrom: activeRrule.date_from,
        dateTo: activeRrule.date_to,
        missingInfo: false,
      };
    });
  }, [buildingUnits]);

  if (activeCleaningsData.length === 0) {
    return null;
  }

  return (
    <Card 
      size="small"
      title={
        <Typography.Text strong style={{ fontSize: '14px' }}>
          {t('buildings.actualPartner')}
        </Typography.Text>
      }
      style={{ minWidth: '400px' }}
    >
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {activeCleaningsData.map((item) => {
          return (
            <div key={item.id} style={{ fontSize: '13px', lineHeight: '1.4' }}>
              <Link to={`/buildings/${item.buildingId}/unit/${item.id}`} style={{ fontWeight: 500 }}>
                {item.address}
              </Link>
              {' – '}
              {item.missingInfo ? (
                <Typography.Text type="warning" style={{ fontStyle: 'italic' }}>
                  {t('buildings.missingCleaningInfo')}
                </Typography.Text>
              ) : (
                <>
                  {item.partner ? (
                    <PartnerLink partner={item.partner} />
                  ) : (
                    <span>-</span>
                  )}
                  {' '}
                  <Typography.Text type="secondary">
                    {moment(item.dateFrom).format('DD.MM.YYYY')} – {item.dateTo ? moment(item.dateTo).format('DD.MM.YYYY') : t('common.empty')}
                  </Typography.Text>
                </>
              )}
            </div>
          );
        })}
      </Space>
    </Card>
  );
};

export default BuildingUnitsActiveCleanings;
